/**
 * Getting the deliverable out of the app.
 *
 * Two outputs, one source: whatever is rendered inside the canvas right now.
 * PDF goes through the browser's own print pipeline — no dependency, no
 * server, and it inherits every chart and layout rule for free. HTML clones the
 * same DOM and inlines the stylesheets so the file stands alone.
 *
 * Both force the **light theme** first. A dark PDF is the wrong artifact: it
 * burns ink, photocopies badly, and doesn't look like the reports these are
 * meant to replace, which are paper-and-ink documents. The app's light theme
 * already exists, so this is a swap rather than a second design.
 */

const THEME_ATTR = 'data-theme';

/** Swap to light, run something, then put the user's theme back. */
async function inLightTheme<T>(run: () => T | Promise<T>): Promise<T> {
  const root = document.documentElement;
  const previous = root.getAttribute(THEME_ATTR);
  root.setAttribute(THEME_ATTR, 'light');
  /* Two frames: one for the attribute to apply, one for charts that re-read
     CSS variables on resize to settle before we snapshot or print. */
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    return await run();
  } finally {
    if (previous) root.setAttribute(THEME_ATTR, previous);
    else root.removeAttribute(THEME_ATTR);
  }
}

/**
 * Print to PDF.
 *
 * `print()` blocks until the dialog closes in most browsers, but not all, so
 * the theme is restored on `afterprint` as well — otherwise a browser that
 * returns immediately would snapshot mid-swap and leave the app in light mode.
 */
export async function exportPdf(): Promise<void> {
  const root = document.documentElement;
  const previous = root.getAttribute(THEME_ATTR);

  const restore = () => {
    if (previous) root.setAttribute(THEME_ATTR, previous);
    else root.removeAttribute(THEME_ATTR);
    window.removeEventListener('afterprint', restore);
  };

  root.setAttribute(THEME_ATTR, 'light');
  window.addEventListener('afterprint', restore);
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  try {
    window.print();
  } finally {
    /* Belt and braces: if `print()` was synchronous, afterprint has already
       fired and this is a no-op; if it wasn't, the listener handles it. */
    restore();
  }
}

/** Every same-origin rule on the page, as text. */
function collectCss(): string {
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) out.push(rule.cssText);
    } catch {
      /* A cross-origin sheet can't be read. We ship none, so skipping is
         correct rather than lossy — but never let it throw. */
    }
  }
  return out.join('\n');
}

/** Strip anything that only makes sense inside the app. */
function clean(root: HTMLElement): void {
  root.querySelectorAll('[data-export-hide]').forEach((el) => el.remove());
  root.querySelectorAll('button').forEach((el) => el.remove());
  root.querySelectorAll('textarea').forEach((el) => el.remove());
  root.querySelectorAll('[contenteditable]').forEach((el) =>
    el.removeAttribute('contenteditable')
  );
}

export interface HtmlExportOptions {
  /** The rendered canvas to capture. */
  node: HTMLElement;
  /** Becomes the document title and the filename. */
  name: string;
  /** Rendered under the title — the workflow's scope line. */
  subtitle?: string;
  /**
   * `light` for a saved artifact — a dark PDF or a dark file mailed to a
   * client is the wrong output. `current` for previewing on screen, where
   * flipping the reader into a theme they didn't choose is jarring and makes
   * "New tab" look unlike the other Present modes.
   */
  theme?: 'light' | 'current';
}

/**
 * Build a standalone HTML file of the current canvas and download it.
 *
 * Images stay as CDN URLs rather than being inlined as base64. The reference
 * report your team produces does the same, and it keeps a 2MB document at
 * ~200KB — the tradeoff is that it needs a network to show artwork, which is
 * true of any report emailed with remote images.
 */
export async function exportHtml(options: HtmlExportOptions): Promise<void> {
  const doc = await buildDocument({ theme: 'light', ...options });
  download(doc, `${slug(options.name.trim() || 'workflow')}.html`);
}

/**
 * Open the same standalone document in a new tab.
 *
 * The blob is deliberately not revoked on a timer — the new tab owns it, and
 * revoking early leaves the reader with a blank page. It's released when the
 * opening document unloads.
 */
export async function openInNewTab(options: HtmlExportOptions): Promise<void> {
  const doc = await buildDocument({ theme: 'current', ...options });
  const url = URL.createObjectURL(new Blob([doc], { type: 'text/html' }));
  window.open(url, '_blank', 'noopener');
}

async function buildDocument({
  node,
  name,
  subtitle,
  theme = 'light',
}: HtmlExportOptions): Promise<string> {
  const capture = () => {
    const clone = node.cloneNode(true) as HTMLElement;
    clean(clone);
    /* `outerHTML`, not `innerHTML` — the grid lives on this container, and
       taking only its children would drop every half/third block back to
       full width in the exported file. */
    return clone.outerHTML;
  };

  /* Only a light capture needs the theme swapped and restored; capturing the
     current theme is just a read. */
  const html = theme === 'light' ? await inLightTheme(capture) : capture();
  const docTheme =
    theme === 'light'
      ? 'light'
      : document.documentElement.getAttribute(THEME_ATTR) ?? 'dark';

  const css = collectCss();
  const title = name.trim() || 'Workflow';
  const stamp = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const doc = `<!doctype html>
<html lang="en" data-theme="${docTheme}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>
${css}
</style>
<style>
  /* Export-only: the canvas is no longer a pane inside an app shell. */
  body {
    margin: 0;
    padding: 40px 32px 64px;
    background: var(--bg-base);
    color: var(--text-primary);
    font-family: var(--font-sans);
  }
  .export-shell { max-width: 1100px; margin: 0 auto; }
  .export-head {
    margin-bottom: 32px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border-default);
  }
  .export-title { margin: 0; font-size: 28px; font-weight: 700; }
  .export-sub { margin: 6px 0 0; color: var(--text-muted); font-size: 14px; }
  .export-foot {
    margin-top: 48px;
    padding-top: 16px;
    border-top: 1px solid var(--border-subtle);
    color: var(--text-muted);
    font-size: 12px;
  }
</style>
</head>
<body>
  <div class="export-shell">
    <header class="export-head">
      <h1 class="export-title">${escapeHtml(title)}</h1>
      ${subtitle ? `<p class="export-sub">${escapeHtml(subtitle)}</p>` : ''}
    </header>
    ${html}
    <footer class="export-foot">
      Generated ${escapeHtml(stamp)} · Figures from Parrot Analytics
    </footer>
  </div>
</body>
</html>`;

  return doc;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function slug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'workflow'
  );
}

function download(contents: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/html' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
